import { BRONCO_SUPPLIER_UUID } from '@/mocks/uuids';
import { COMPLETE_TRANSLATION_FRAGMENT } from './fragments/translation-fragments';
import { getDemoObiForBroncoRestaurant } from '@/mocks/dsr-analytics/obi-data';
import {
  GetItemsOfInterestQuery,
  GetItemsOfInterestQueryVariables,
  ObiItem,
} from '@/types';
import { gql } from '@apollo/client';
import { useMemo } from 'react';
import { useWrappedQuery } from './use-wrapped-query';

export const GET_ITEMS_OF_INTEREST_QUERY = gql`
  ${COMPLETE_TRANSLATION_FRAGMENT}

  query getItemsOfInterest(
    $supplierUUID: uuid!
    $restaurantUUID: uuid!
    $fromDate: String!
  ) {
    getObiItems(
      supplier_id: $supplierUUID
      restaurant_id: $restaurantUUID
      filter_from: $fromDate
    ) {
      obi_items {
        variant_pack_uuid
        title
        subtitle {
          ...CompleteTranslationFields
        }
        obi_item_type
        event_time
        variant_pack {
          uuid
          item {
            photo_url_list
            uuid
          }
        }
      }
    }
  }
`;

const groupCarouselItems = (
  carouselData: Array<ObiItem>,
  groupLength: number,
) => {
  const groupedItems: Array<Array<ObiItem>> = [];

  carouselData.forEach((item, index) => {
    const groupIndex = Math.floor(index / groupLength);
    if (!groupedItems[groupIndex]) {
      groupedItems[groupIndex] = [];
    }
    groupedItems[groupIndex].push(item);
  });

  return groupedItems;
};

export const useObiItemsOfInterest = (
  restaurantUUID: string,
  supplierUUID: string,
  previousDays: number,
  groupLength: number,
) => {
  const isBroncoDemo = supplierUUID === BRONCO_SUPPLIER_UUID;

  const demoItems = useMemo(() => {
    if (isBroncoDemo) {
      return getDemoObiForBroncoRestaurant(restaurantUUID);
    }
  }, [isBroncoDemo, restaurantUUID]);
  const hasDemoData = demoItems !== undefined;

  // set date for X amount of days from today
  const d = new Date();
  d.setDate(d.getDate() - previousDays);
  const fromDateStr = d.toISOString().split('T')[0];

  const { loading, error, data } = useWrappedQuery<
    GetItemsOfInterestQuery,
    GetItemsOfInterestQueryVariables
  >(GET_ITEMS_OF_INTEREST_QUERY, {
    context: {
      noBatch: true,
    },
    skip: hasDemoData,
    variables: {
      fromDate: fromDateStr,
      restaurantUUID,
      supplierUUID,
    },
  });

  const liveItems = data?.getObiItems?.obi_items as Array<ObiItem> | undefined;
  const obiItems = hasDemoData ? (demoItems as Array<ObiItem>) : liveItems;

  return {
    data: obiItems ? groupCarouselItems(obiItems, groupLength) : undefined,
    error: hasDemoData ? undefined : error,
    loading: hasDemoData ? false : loading,
  };
};
